import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Popover,
  Tooltip,
  Button,
  Modal,
} from "@mui/material";
import axios from "axios";

import CloseIcon from "@mui/icons-material/Close";
import "./MississippiMap.css";
import Carousel from "../Carousel/Carousel";
import ImageCarousel from "../ImageCarousel/ImageCarousel";
import imgProfAndrew from "../../assets/Images_HubStaff/ProfAndrew.jpeg";
import imgMaeve from "../../assets/Images_HubStaff/maevelosen.jpg";
import imgAvantika from "../../assets/Images_HubStaff/Avantika1.jpeg";
import imgPhil from "../../assets/Images_HubStaff/Phill.png";
import imgJamie from "../../assets/Images_HubStaff/Jamie.jpg";
import noImage from "../../assets/noImage.jpg";
import MenuIcon from "@mui/icons-material/Menu";
import API_BASE_URL from "../../utils/api";

const VB = { x: 600, y: 140, w: 1500, h: 2575 };

const generatePixelTrail = () => {
  const pixels = [];
  const baseX = 1570;
  const baseY = 2000;
  const pixelImageMap = {
    2: [imgMaeve],
    3: [imgPhil, imgProfAndrew],
    5: [imgAvantika, imgJamie],
  };

  let rightShiftPerLevel = 170;

  let id = 1;

  const levelPixelConfigs = [
    [{ size: 140, xOffset: 0, yOffset: 0 }],
    [
      { size: 170, xOffset: -30, yOffset: 0 },
      { size: 110, xOffset: -40, yOffset: 50 },
    ], // Level 1: 2 pixels
    [
      { size: 170, xOffset: -80, yOffset: 0 },
      { size: 100, xOffset: -10, yOffset: 60 },
      { size: 170, xOffset: -40, yOffset: 60 },
    ], // Level 2: 3 pixels
    [
      { size: 210, xOffset: -50, yOffset: -10 },
      { size: 140, xOffset: -10, yOffset: 110 },
    ], // Level 3: 2 pixels
    [
      { size: 170, xOffset: 20, yOffset: 0 },
      { size: 110, xOffset: 60, yOffset: 120 },
    ], // Level 4: 2 pixels
    [{ size: 140, xOffset: -20, yOffset: 0 }], // Level 5: 1 pixel
    [
      { size: 110, xOffset: -260, yOffset: 190 },
      { size: 300, xOffset: -80, yOffset: 10 },
    ], // Level 6: 2 pixels (curve starts)
    [
      { size: 210, xOffset: -170, yOffset: 160 },
      { size: 170, xOffset: 40, yOffset: 90 },
      { size: 170, xOffset: 10, yOffset: 90 },
    ], // Level 7: 3 pixels
    [
      { size: 220, xOffset: -60, yOffset: 200 },
      { size: 170, xOffset: 100, yOffset: 140 },
      { size: 220, xOffset: 0, yOffset: 20 },
    ], // Level 8: 3 pixels
    [
      { size: 170, xOffset: -30, yOffset: 280 },
      { size: 170, xOffset: 30, yOffset: 520 },
    ],
  ];
  const levelSpacingX = [
    200, // Level 0
    350, // Level 1
    260, // Level 2
    320, // Level 3
    320, // Level 4
    200, // Level 5
    190, // Level 6
    220, // Level 7
    230, // Level 8
    240, // Level 9
  ];
  const levelSpacingY = [
    350, // Level 0
    270, // Level 1
    270, // Level 2
    270, // Level 3
    270, // Level 4
    260, // Level 5
    335, // Level 6
    350, // Level 7
    350, // Level 8
    350, // Level 9
  ];

  const levels = levelPixelConfigs.length;

  for (let level = 0; level < levels; level++) {
    const pixelConfigs = levelPixelConfigs[level];
    const numPixels = pixelConfigs.length;
    const spacingX = levelSpacingX[level];
    const spacingY = levelSpacingY[level];

    for (let i = 0; i < numPixels; i++) {
      const { size, xOffset = 0, yOffset = 0 } = pixelConfigs[i];

      let x, y;

      if (level < 6) {
        const offset = (i - (numPixels - 1) / 2) * spacingX;
        x = baseX + Math.pow(level, 1.2) * 15 + offset + xOffset;
        y = baseY - level * spacingY + yOffset;
      } else {
        const arcProgress = level - 4;
        const horizontalSpacing = levelSpacingX[level];
        const verticalSpacing = levelSpacingY[level];

        const horizontalCurveShift = arcProgress * horizontalSpacing;
        const verticalCurveDrop = Math.pow(arcProgress, 1.2) * 30;

        x = baseX + Math.pow(4, 1.2) * 15 + horizontalCurveShift + xOffset;
        y =
          baseY -
          (4 * spacingY + verticalCurveDrop) +
          (i - 1) * verticalSpacing +
          yOffset;
      }
      const pixelImages = pixelImageMap[id] || [];
      const placeholder = pixelImages.length > 0 ? pixelImages[0] : noImage;

      pixels.push({
        id: id++,
        x,
        y,
        size,
        title: `Pixel ${id}`,
        images: pixelImages,
        placeholder,
      });
    }
  }

  return pixels;
};

const MississippiMap = forwardRef((props, ref) => {
  const stopAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      e.nativeEvent &&
      typeof e.nativeEvent.stopImmediatePropagation === "function"
    ) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };
  const closingRef = useRef(false);
  const beginCloseBlock = (ms = 400) => {
    closingRef.current = true;
    setTimeout(() => {
      closingRef.current = false;
    }, ms);
  };

  const closeGallery = useCallback(() => {
    setGalleryOnly(false);
    setLocked(false);
    setSelectedId(null);
    setHoveredId(null);
    setDialogPixel(null);
  }, []);

  const logoSrc = noImage;

  const [locked, setLocked] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [dialogPixel, setDialogPixel] = useState(null);
  const [dialogSide, setDialogSide] = useState("right");
  const [dialogPos, setDialogPos] = useState({ left: 0, top: 0 });
  const dialogRef = useRef(null);
  const [arrow, setArrow] = useState(null);
  const [contentSizeTick, setContentSizeTick] = useState(0);
  const svgRef = useRef(null);

  const containerRef = useRef(null);
  const [listOpen, setListOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const openMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);
  const hasImages = (p) => Array.isArray(p.images) && p.images.length > 0;
  const [dialogW, setDialogW] = useState(380);
  const [galleryOnly, setGalleryOnly] = useState(false);
  const [groups, setGroups] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const PIXEL_GALLERY_MAP = {
    Workshop2025: 13,
    Visit_to_choctaw: 14,
    Conference: 16,
    Visit_to_jimmie_rodgers: 17,
    GrantRecipientVisits: 18,
    SymposiumDH: 19,
    LocalCommunity: 20,
    "SummerResidency2026-classroomSessions": 12,
    "SummerResidency2026-MuseumVisits": 11,
    symposium2026: 10,
  };

  const getDayNumber = (title) => {
    const m = /day\s*(\d+)/i.exec(title || "");
    return m ? parseInt(m[1], 10) : Number.POSITIVE_INFINITY;
  };

  const sortImages = (images = []) =>
    [...images]
      .filter((im) => im?.url)
      .sort((a, b) => {
        const da = getDayNumber(a.title),
          db = getDayNumber(b.title);
        if (da !== db) return da - db;
        const oa = Number(a.order ?? 0),
          ob = Number(b.order ?? 0);
        if (oa !== ob) return oa - ob;
        return String(a.key).localeCompare(String(b.key));
      });

  const attachGalleries = (basePixels, groups, mapping) => {
    const bySlug = Object.fromEntries((groups || []).map((g) => [g.slug, g]));
    const result = basePixels.map((p) => ({
      ...p,
      title: `Pixel ${p.id}`,
      images: [],
      placeholder: noImage,
    }));

    Object.entries(mapping).forEach(([slug, pixelId]) => {
      const g = bySlug[slug];
      const i = result.findIndex((p) => p.id === pixelId);
      if (!g || i === -1) return;

      const sorted = sortImages(g.images || []);
      result[i] = {
        ...result[i],
        title: g.tag || result[i].title,
        images: sorted,
        placeholder: sorted[0]?.url || noImage,
      };
    });

    return result;
  };
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/pixels`, { withCredentials: true })
      .then((res) => setGroups(res.data || []))
      .catch((err) => console.error("/pixels fetch failed", err));
  }, []);

  const basePixels = useMemo(() => generatePixelTrail(), []);

  const pixels = useMemo(() => {
    return attachGalleries(basePixels, groups, PIXEL_GALLERY_MAP);
  }, [basePixels, groups]);
  useEffect(() => {
    const updatePos = () => {
      if (!svgRef.current || !containerRef.current || !dialogPixel) return;

      const svgRect = svgRef.current.getBoundingClientRect();
      const contRect = containerRef.current.getBoundingClientRect();

      const sx = svgRect.width / VB.w;
      const sy = svgRect.height / VB.h;
      const s = Math.min(sx, sy);
      const contentW = VB.w * s;
      const contentH = VB.h * s;
      const hPad = (svgRect.width - contentW) / 2;
      const vPad = (svgRect.height - contentH) / 2;

      const baseLeft = svgRect.left - contRect.left + hPad;
      const baseTop = svgRect.top - contRect.top + vPad;

      const pxLeft = baseLeft + (dialogPixel.x - VB.x) * s;
      const pxRight = baseLeft + (dialogPixel.x - VB.x + dialogPixel.size) * s;
      const pxTop = baseTop + (dialogPixel.y - VB.y) * s;

      const pxCenterX =
        baseLeft + (dialogPixel.x - VB.x + dialogPixel.size / 2) * s;
      const pxCenterY =
        baseTop + (dialogPixel.y - VB.y + dialogPixel.size / 2) * s;
      const pxBottom = baseTop + (dialogPixel.y - VB.y + dialogPixel.size) * s;

      const MARGIN = 8;
      const GAP = 10;
      const MIN_W = Math.min(200, contRect.width * 0.95);
      const MAX_W = Math.min(320, contRect.width * 0.95);

      let desiredW = Math.min(MAX_W, Math.max(MIN_W, contRect.width * 0.8));

      const arrowFromW = (w) =>
        Math.max(10, Math.min(22, Math.round(w * 0.06)));
      let ARROW_W = arrowFromW(desiredW);

      const spaceRight = contRect.width - MARGIN - (pxRight + GAP + ARROW_W);
      const spaceLeft = pxLeft - GAP - ARROW_W - MARGIN;
      const spaceBelow =
        contRect.height -
        MARGIN -
        (pxBottom + GAP + Math.round(ARROW_W * 0.55));
      const spaceAbove = pxTop - GAP - Math.round(ARROW_W * 0.55) - MARGIN;

      let side = "right";
      if (spaceRight < MIN_W && spaceLeft >= MIN_W) side = "left";
      else if (spaceRight < MIN_W && spaceLeft < MIN_W) {
        if (spaceBelow >= spaceAbove) side = "bottom";
        else side = "top";
      }

      if (side === "right" && spaceRight < desiredW) {
        desiredW = Math.max(MIN_W, Math.min(desiredW, spaceRight));
      }
      if (side === "left" && spaceLeft < desiredW) {
        desiredW = Math.max(MIN_W, Math.min(desiredW, spaceLeft));
      }
      ARROW_W = arrowFromW(desiredW);

      const measuredH = dialogRef.current?.offsetHeight || 280;
      const halfH = Math.max(80, Math.min(220, Math.round(measuredH / 2)));

      let left, top;

      if (side === "bottom") {
        left = pxCenterX - desiredW / 2;
        left = Math.max(
          MARGIN,
          Math.min(left, contRect.width - desiredW - MARGIN)
        );

        const arrowH = Math.round(ARROW_W * 0.55);
        top = pxBottom + GAP + arrowH;

        const maxTop = contRect.height - MARGIN - measuredH;
        if (top > maxTop) top = maxTop;
      } else if (side === "top") {
        left = pxCenterX - desiredW / 2;
        left = Math.max(
          MARGIN,
          Math.min(left, contRect.width - desiredW - MARGIN)
        );
        const arrowH = Math.round(ARROW_W * 0.55);
        top = pxTop - GAP - arrowH - measuredH;
        if (top < MARGIN) top = MARGIN;
      } else {
        left =
          side === "right"
            ? pxRight + GAP + ARROW_W
            : pxLeft - GAP - ARROW_W - desiredW;

        left = Math.max(
          MARGIN,
          Math.min(left, contRect.width - desiredW - MARGIN)
        );
        top = Math.max(
          MARGIN + halfH,
          Math.min(pxCenterY, contRect.height - MARGIN - halfH)
        );
      }

      setDialogSide(side);
      setDialogPos({ left, top });
      setDialogW(desiredW);
      const maxH = Math.min(420, contRect.height - 2 * MARGIN);
      if (dialogRef.current) {
        dialogRef.current.style.maxHeight = `${maxH}px`;
        dialogRef.current.style.overflowY = "auto";
      }
    };

    updatePos();
    const ro = new ResizeObserver(updatePos);
    if (containerRef.current) ro.observe(containerRef.current);
    if (dialogRef.current) ro.observe(dialogRef.current);

    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("resize", updatePos);
      ro.disconnect();
    };
  }, [dialogPixel, contentSizeTick]);
  const handleEnter = (pixel) => {
    if (locked) return;
    if (!hasImages(pixel)) {
      setHoveredId(null);
      setDialogPixel(null);
      return;
    }
    setHoveredId(pixel.id);
    setDialogPixel(pixel);
  };

  const handleLeave = (pixel) => {
    if (locked) return;
    if (hoveredId === pixel.id) setHoveredId(null);
    setDialogPixel(null);
  };

  const handleClick = (pixel) => {
    if (closingRef.current) return;
    setLocked(true);
    setSelectedId(pixel.id);
    setDialogPixel(pixel);
  };

  const handleCloseDialog = () => {
    setLocked(false);
    setSelectedId(null);
    setHoveredId(null);
    setDialogPixel(null);
    setGalleryOnly(false);
  };
  const handleJumpToPixel = (id) => {
    if (closingRef.current) return;

    const p = pixels.find((px) => px.id === id);
    if (!p) return;
    setLocked(true);
    setSelectedId(id);
    setHoveredId(null);
    setDialogPixel(p);
    setListOpen(false);
  };
  const arrowW = Math.max(10, Math.min(22, Math.round(dialogW * 0.06)));
  const arrowH = Math.round(arrowW * 0.55);
  useEffect(() => {
    setGalleryOnly(false);
    setActiveIdx(0);
  }, [dialogPixel?.id]);
  useImperativeHandle(ref, () => ({
    openGallery: (pixelId) => {
      if (closingRef.current) return;

      const target =
        (pixelId && pixels.find((p) => p.id === pixelId && hasImages(p))) ||
        pixels.find(hasImages) ||
        null;
      if (!target) return;

      setLocked(true);
      setSelectedId(target.id);
      setDialogPixel(target);
      setActiveIdx(0);
      setGalleryOnly(true);
    },
    isGalleryOpen: () => galleryOnly,
  }));

  return (
    <Box className="mississippi-map" sx={{ width: "100%", p: 4 }}>
      {/*  <Typography variant="h4" align="center" gutterBottom>
        Mississippi Map Pixel Explorer
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        color="text.secondary"
        gutterBottom
      >
        Hover or click a square to preview details. Click locks the dialog;
        close to unlock.
      </Typography>*/}
      <Box
        sx={{
          display: "flex",
          gap: 4,
          width: "100%",
          maxWidth: 1400,
          mx: "auto",
          p: 2,
        }}
      >
        <Box
          sx={{
            flex: { xs: "1 1 50%", md: "1 1 60%" },
            overflow: "visible",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box sx={{ position: "relative" }} ref={containerRef}>
            <IconButton
              onClick={openMenu}
              aria-label="Open pixel list"
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                zIndex: 100,
                background: "rgba(255,255,255,0.9)",
                boxShadow: 2,
                "&:hover": { background: "rgba(255,255,255,1)" },
              }}
              size="small"
            >
              <MenuIcon fontSize="small" />
            </IconButton>
            <Popover
              open={Boolean(menuAnchor)}
              anchorEl={menuAnchor}
              onClose={closeMenu}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{
                paper: {
                  sx: {
                    width: 300,
                    maxHeight: 360,
                    p: 1,
                  },
                },
              }}
            >
              <List dense>
                <Divider sx={{ mb: 1 }} />
                {pixels.filter(hasImages).map((p) => (
                  <ListItemButton
                    key={p.id}
                    onClick={() => {
                      handleJumpToPixel(p.id);
                      closeMenu();
                    }}
                    selected={p.id === selectedId}
                  >
                    <ListItemText
                      primary={p.title || `Pixel ${p.id}`}
                      secondary={`${p.images.length} image(s)`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Popover>
            {dialogPixel && (
              <Box
                ref={dialogRef}
                className="pixel-dialog"
                sx={{
                  position: "absolute",
                  p: 1.25,
                  left: dialogPos.left,
                  top: dialogPos.top,
                  transform:
                    dialogSide === "bottom" ? "none" : "translateY(-50%)",
                  width: dialogW,
                  zIndex: 5,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.18)",
                  boxShadow: "0 6px 22px rgba(0,0,0,0.14)",
                  backgroundColor: "rgba(255,255,255,0.86)",
                  backdropFilter: "saturate(140%) blur(8px)",
                  WebkitBackdropFilter: "saturate(140%) blur(8px)",
                  pointerEvents: locked ? "auto" : "none",

                  "&::before": {
                    content: '""',
                    position: "absolute",
                    width: 0,
                    height: 0,
                    pointerEvents: "none",
                    ...(dialogSide === "right" && {
                      top: "50%",
                      transform: "translateY(-50%)",
                      left: -arrowW,
                      borderStyle: "solid",
                      borderWidth: `${arrowH}px ${arrowW}px ${arrowH}px 0`,
                      borderColor:
                        "transparent rgba(0,0,0,0.18) transparent transparent",
                    }),
                    ...(dialogSide === "left" && {
                      top: "50%",
                      transform: "translateY(-50%)",
                      right: -arrowW,
                      borderStyle: "solid",
                      borderWidth: `${arrowH}px 0 ${arrowH}px ${arrowW}px`,
                      borderColor:
                        "transparent transparent transparent rgba(0,0,0,0.18)",
                    }),
                    ...(dialogSide === "bottom" && {
                      top: -arrowH,
                      left: "50%",
                      transform: "translateX(-50%)",
                      borderStyle: "solid",
                      borderWidth: `${arrowH}px ${arrowW}px 0 ${arrowW}px`,
                      borderColor:
                        "rgba(0,0,0,0.18) transparent transparent transparent",
                    }),
                    ...(dialogSide === "top" && {
                      bottom: -arrowH,
                      left: "50%",
                      transform: "translateX(-50%)",
                      borderStyle: "solid",
                      borderWidth: `0 ${arrowW}px ${arrowH}px ${arrowW}px`,
                      borderColor:
                        "transparent transparent rgba(0,0,0,0.18) transparent",
                    }),
                  },

                  "&::after": {
                    content: '""',
                    position: "absolute",
                    width: 0,
                    height: 0,
                    pointerEvents: "none",
                    ...(dialogSide === "right" && {
                      top: "50%",
                      transform: "translateY(-50%)",
                      left: -(arrowW - 1),
                      borderStyle: "solid",
                      borderWidth: `${arrowH - 1}px ${arrowW - 1}px ${arrowH - 1}px 0`,
                      borderColor:
                        "transparent rgba(255,255,255,0.86) transparent transparent",
                    }),
                    ...(dialogSide === "left" && {
                      top: "50%",
                      transform: "translateY(-50%)",
                      right: -(arrowW - 1),
                      borderStyle: "solid",
                      borderWidth: `${arrowH - 1}px 0 ${arrowH - 1}px ${arrowW - 1}px`,
                      borderColor:
                        "transparent transparent transparent rgba(255,255,255,0.86)",
                    }),
                    ...(dialogSide === "bottom" && {
                      top: -(arrowH - 1),
                      left: "50%",
                      transform: "translateX(-50%)",
                      borderStyle: "solid",
                      borderWidth: `${arrowH - 1}px ${arrowW - 1}px 0 ${arrowW - 1}px`,
                      borderColor:
                        "rgba(255,255,255,0.86) transparent transparent transparent",
                    }),
                    ...(dialogSide === "top" && {
                      bottom: -(arrowH - 1),
                      left: "50%",
                      transform: "translateX(-50%)",
                      borderStyle: "solid",
                      borderWidth: `0 ${arrowW - 1}px ${arrowH - 1}px ${arrowW - 1}px`,
                      borderColor:
                        "transparent transparent rgba(255,255,255,0.86) transparent",
                    }),
                  },
                }}
              >
                {!galleryOnly && (
                  <Box sx={{ width: "100%", mb: 1 }}>
                    <Box
                      sx={{
                        position: "relative",
                        display: "grid",
                        gridTemplateColumns: "auto 1fr auto",
                        alignItems: "start",
                        columnGap: 1,
                        py: 0.25,
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{ visibility: "hidden" }}
                        aria-hidden="true"
                        tabIndex={-1}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>

                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: "center",
                          lineHeight: 1.2,
                          px: 1,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                          hyphens: "auto",
                          fontWeight: "700",
                          fontSize: { xs: 14, sm: 15, md: 16 },
                        }}
                      >
                        {dialogPixel?.title}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          console.log("close icon clicked");
                          e.stopPropagation();

                          beginCloseBlock();
                          handleCloseDialog();
                        }}
                        aria-label="Close"
                        sx={{ justifySelf: "end" }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {dialogPixel?.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", mt: 0.5 }}
                      >
                        {dialogPixel.description}
                      </Typography>
                    )}
                  </Box>
                )}
                {dialogPixel.images?.length > 0 ? (
                  <>
                    {!galleryOnly && (
                      <>
                        <ImageCarousel
                          className="ic-wrap"
                          images={(dialogPixel.images || []).map(
                            (im) => im.url || im
                          )}
                          interval={5000}
                          aspectRatio="4/3"
                          rounded={false}
                          onIndexChange={(i) => {
                            setActiveIdx(i);
                            setContentSizeTick((t) => t + 1);
                          }}
                          initialIndex={activeIdx}
                        />
                        {dialogPixel.images?.[activeIdx] && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">
                              {dialogPixel.images[activeIdx].title ||
                                dialogPixel.title}
                            </Typography>
                            {!!dialogPixel.images[activeIdx].desc && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {dialogPixel.images[activeIdx].desc}
                              </Typography>
                            )}
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 1,
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setGalleryOnly(true)}
                            sx={{
                              mt: 1,
                              borderRadius: 999,
                              px: 2,
                              fontSize: 12,
                              textTransform: "none",
                              borderColor: "rgba(0,0,0,0.25)",
                              color: "#333",
                              backgroundColor: "rgba(255,255,255,0.85)",
                              "&:hover": {
                                backgroundColor: "#fff",
                                borderColor: "#1976d2",
                                color: "#1976d2",
                              },
                            }}
                          >
                            Closer look
                          </Button>
                        </Box>
                      </>
                    )}

                    <Modal
                      open={galleryOnly}
                      onClose={() => {
                        beginCloseBlock();
                        requestAnimationFrame(() => closeGallery());
                      }}
                      keepMounted
                      slotProps={{
                        backdrop: {
                          onClick: (e) => {
                            e.stopPropagation();
                            beginCloseBlock();
                            requestAnimationFrame(() => closeGallery());
                          },
                        },
                      }}
                    >
                      <Box
                        className="gallery-fs"
                        sx={{
                          position: "fixed",
                          inset: 0,
                          height: "100dvh",
                          bgcolor: "rgba(0,0,0,0.96)",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden", // keep layout stable
                        }}
                      >
                        <Box
                          sx={{
                            px: 2,
                            pt: "max(8px, env(safe-area-inset-top, 8px))",
                            pb: 1,
                            position: "sticky",
                            top: 0,
                            zIndex: 5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            background:
                              "linear-gradient(to bottom, rgba(0,0,0,0.65), rgba(0,0,0,0))",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                              color: "#fff",
                              fontWeight: 600,
                              textAlign: "center",
                              maxWidth: "calc(100% - 96px)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              pointerEvents: "none",
                            }}
                          >
                            {dialogPixel?.title}
                          </Typography>
                          <IconButton
                            aria-label="Close"
                            onClick={(e) => {
                              e.stopPropagation();
                              beginCloseBlock();
                              requestAnimationFrame(() => closeGallery());
                            }}
                            sx={{ color: "#fff" }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: { xs: 0, md: 2 },
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              maxWidth: 1200,
                              display: "flex",
                              flexDirection: "column",
                              minHeight: 0,
                            }}
                          >
                            <Box
                              sx={{ flex: 1, minHeight: 0, display: "flex" }}
                            >
                              <ImageCarousel
                                className="ic-wrap ic-fullscreen"
                                images={(dialogPixel?.images || []).map(
                                  (im) => im.url || im
                                )}
                                interval={5000}
                                rounded={false}
                                onIndexChange={(i) => {
                                  setActiveIdx(i);
                                  setContentSizeTick((t) => t + 1);
                                }}
                                initialIndex={activeIdx}
                              />
                            </Box>

                            {dialogPixel?.images?.[activeIdx] && (
                              <Box
                                sx={{
                                  px: 2,
                                  pb: "max(14px, env(safe-area-inset-bottom, 14px))",
                                  pt: 1.5,
                                  maxHeight: {
                                    xs: "34vh",
                                    sm: "28vh",
                                    md: "22vh",
                                  }, // key
                                  overflowY: "auto", // key
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: "#fff",
                                    fontWeight: 700,
                                    textAlign: "center",
                                    mb: 0.75,
                                  }}
                                >
                                  {dialogPixel.images[activeIdx].title ||
                                    dialogPixel.title}
                                </Typography>
                                {!!dialogPixel.images[activeIdx].desc && (
                                  <Typography
                                    sx={{
                                      color: "rgba(255,255,255,0.85)",
                                      textAlign: "center",
                                      whiteSpace: "pre-wrap",
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {dialogPixel.images[activeIdx].desc}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Modal>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No images are linked to this pixel.
                  </Typography>
                )}
              </Box>
            )}
            {dialogPixel && arrow && (
              <Box
                className="bubble-arrow"
                sx={{
                  position: "fixed",
                  left: arrow.x,
                  top: arrow.y,
                  width: 28,
                  height: 18,
                  transform: `translate(-50%, -50%) rotate(${arrow.angle}deg)`,
                  zIndex: 51,
                  pointerEvents: "none",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 100,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "18px solid rgba(255,255,255,0.9)",
                    borderTop: "9px solid transparent",
                    borderBottom: "9px solid transparent",
                    filter: "drop-shadow(0 0 1px rgba(0,0,0,0.25))",
                  },
                }}
              />
            )}
            <svg
              ref={svgRef}
              viewBox={`${VB.x} ${VB.y} ${VB.w} ${VB.h}`}
              width="700"
              height="500"
              style={{
                width: "100%",
                height: "auto",
              }}
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <path
                d="M 1257.500 166.019 C 1252.101 167.830, 1246.643 173.898, 1245.032 179.881 C 1244.366 182.355, 1244.519 189.984, 1245.514 204 C 1246.315 215.275, 1246.977 225.072, 1246.985 225.770 C 1247.007 227.641, 1234.739 235.255, 1215.742 245.162 C 1181.935 262.792, 1163.726 278.621, 1151.161 301.303 C 1139.526 322.305, 1133.764 343.813, 1129.541 382 C 1123.039 440.794, 1111.520 484.396, 1096.859 505.705 C 1093.026 511.275, 1090.525 512.847, 1081.744 515.202 C 1066.396 519.318, 1053.025 528.543, 1024 555.038 C 995.771 580.806, 974.705 601.462, 973.353 604.697 C 971.488 609.161, 971.589 611.967, 974.644 640.676 L 977.322 665.853 950.842 693.676 C 936.277 708.979, 918.382 727.800, 911.074 735.500 C 903.766 743.200, 896.935 751.170, 895.894 753.210 C 893.593 757.718, 893.527 762.430, 895.475 783 C 898.258 812.384, 899.205 833.047, 897.845 834.686 C 897.167 835.504, 891.411 840.020, 885.056 844.722 C 871.035 855.094, 855.782 870.182, 849.500 879.893 C 846.946 883.842, 843.760 890.295, 842.421 894.233 C 837.193 909.603, 828.219 955.908, 825.935 979.296 C 823.833 1000.823, 826.918 1009.241, 843.090 1026.110 C 858.741 1042.435, 865.862 1055.534, 871.416 1078.217 C 878.957 1109.017, 878.802 1131.905, 870.883 1156.759 C 867.238 1168.199, 865.181 1173.246, 855.752 1193.884 C 851.887 1202.345, 848.524 1211.120, 848.280 1213.384 C 848.035 1215.648, 848.990 1233.700, 850.400 1253.500 C 853.499 1296.989, 854.979 1331.192, 855.009 1360 C 855.049 1398.910, 857.277 1420.146, 863.077 1436.885 C 866.642 1447.173, 870.486 1454.293, 882.186 1472.274 L 891.148 1486.049 888.330 1494.274 C 885.881 1501.424, 885.512 1504.005, 885.509 1514 C 885.506 1523.257, 885.960 1526.949, 887.835 1532.927 C 890.842 1542.512, 897.101 1555.461, 905.995 1570.500 C 920.780 1595.499, 923.137 1602.862, 918.980 1611.074 C 917.747 1613.508, 912.860 1620.587, 908.119 1626.804 C 883.176 1659.516, 877.750 1670.497, 866.023 1712 C 857.031 1743.821, 845.530 1766.193, 826.509 1788.861 C 816.654 1800.606, 803.772 1812.112, 786.190 1824.874 C 772.360 1834.913, 760.015 1846.600, 754.186 1855.175 C 750.033 1861.283, 742.947 1875.447, 739.013 1885.500 C 733.702 1899.077, 725.259 1914.877, 715.503 1929.500 C 705.016 1945.218, 699.584 1955.347, 698.072 1962 C 696.624 1968.372, 696.789 1977.497, 698.480 1984.500 C 700.813 1994.163, 700.381 1999.856, 696.374 2012.268 C 691.816 2026.386, 678.640 2059.348, 653.508 2119.500 C 639.954 2151.940, 633.716 2168.049, 633.373 2171.500 C 633.012 2175.117, 634.255 2183.276, 637.866 2201 C 640.611 2214.475, 643.410 2229.462, 644.087 2234.304 L 645.318 2243.107 641.295 2248.812 C 634.535 2258.398, 631.642 2264.804, 631.178 2271.215 C 630.597 2279.255, 632.976 2285.246, 638.275 2289.084 C 645.321 2294.188, 650.413 2295.146, 675 2295.991 C 706.700 2297.080, 1019.686 2300.610, 1248 2302.453 C 1353.875 2303.308, 1445.143 2304.276, 1450.818 2304.604 C 1459.881 2305.129, 1461.054 2305.402, 1460.461 2306.851 C 1457.514 2314.050, 1453.694 2327.014, 1451.500 2337.258 C 1450.058 2343.991, 1447.937 2352.425, 1446.787 2356 C 1444.509 2363.085, 1436.237 2381.592, 1428.133 2397.738 C 1420.700 2412.546, 1418.012 2421.323, 1417.177 2433.511 C 1416.057 2449.873, 1417.251 2462.988, 1420.833 2473.671 C 1427.219 2492.712, 1445.671 2526.489, 1470.888 2565.296 C 1499.400 2609.175, 1503.949 2618.764, 1508.442 2644.457 C 1512.410 2667.148, 1516.150 2675.274, 1528.064 2687.093 C 1547.031 2705.908, 1570.183 2716.292, 1589.521 2714.658 C 1613.350 2712.643, 1628.743 2700.977, 1656.281 2664.061 C 1665.077 2652.270, 1674.298 2640.457, 1676.773 2637.811 C 1682.065 2632.154, 1682.120 2632.147, 1702.292 2634.618 C 1711.672 2635.768, 1719.847 2636.082, 1730.500 2635.702 C 1746.432 2635.134, 1751.524 2634.285, 1788.500 2626.031 C 1818.599 2619.313, 1840.795 2616.024, 1860.893 2615.305 L 1877.500 2614.711 1891.588 2620.332 C 1914.322 2629.404, 1918.736 2630.038, 1973.710 2632.140 L 2005.920 2633.372 2012.710 2639.966 C 2021.785 2648.778, 2029.085 2652.268, 2038.500 2652.294 C 2047.581 2652.320, 2054.745 2649.555, 2062.542 2643.015 C 2069.053 2637.554, 2070.514 2636.731, 2076.545 2635.122 C 2081.673 2633.754, 2088.456 2627.987, 2091.151 2622.703 C 2094.868 2615.419, 2096.929 2601.906, 2098.124 2577 C 2099.430 2549.780, 2098.714 2366.997, 2097.050 2302.500 C 2094.091 2187.848, 2089.188 2034.798, 2082.538 1849.522 L 2081.103 1809.544 2087.516 1756.022 C 2093.316 1707.606, 2118.300 1490.734, 2189.470 871 C 2215.538 644.004, 2222.763 576.040, 2235.447 438.500 C 2245.869 325.485, 2250.734 249.783, 2248.089 241.771 C 2247.432 239.779, 2243.744 234.629, 2239.894 230.325 C 2236.043 226.021, 2231.620 220.003, 2230.063 216.950 C 2228.507 213.898, 2223.581 205.873, 2219.117 199.116 C 2214.653 192.359, 2211 186.224, 2211 185.482 C 2211 183.330, 2201.746 174.421, 2196.991 171.995 C 2194.600 170.776, 2190.137 169.592, 2187.072 169.366 C 2160.393 167.391, 1263.042 164.160, 1257.500 166.019 M 1264.987 187.750 C 1264.980 189.262, 1265.506 198.825, 1266.157 209 C 1266.808 219.175, 1267.011 229.228, 1266.608 231.340 C 1265.067 239.419, 1254.835 247.281, 1226.170 262.411 C 1217.387 267.046, 1208.243 272.095, 1205.850 273.630 C 1169.355 297.045, 1155.425 324.706, 1149.515 385.500 C 1144.989 432.056, 1133.098 481.538, 1120.690 505.456 C 1116.096 514.313, 1108.798 523.926, 1103.790 527.720 C 1099.454 531.004, 1095.246 532.763, 1086.166 535.085 C 1072.690 538.532, 1064.620 544.733, 1022.015 584.384 C 1009.082 596.421, 997.033 607.683, 995.240 609.411 L 991.979 612.554 995.275 642.736 L 998.572 672.918 971.036 701.806 C 955.891 717.694, 936.858 737.683, 928.741 746.226 L 913.981 761.759 915.967 784.129 C 917.059 796.433, 918.254 813.487, 918.622 822.027 C 919.581 844.265, 919.278 844.793, 895.500 862.374 C 881.974 872.375, 868.259 886.678, 864.190 895.027 C 857.493 908.769, 845.878 967.520, 845.796 988.069 C 845.752 999.008, 846.607 1000.648, 859.154 1013.709 C 873.821 1028.976, 881.087 1041.184, 887.897 1062 C 893.594 1079.414, 896.093 1093.773, 896.708 1112.627 C 897.707 1143.267, 893.735 1159.584, 875.617 1199.275 L 868.073 1215.804 869.998 1242.152 C 873.188 1285.818, 874.038 1304.429, 874.991 1351.500 C 875.493 1376.250, 876.404 1400.931, 877.017 1406.347 C 879.213 1425.748, 884.117 1439.218, 894.331 1453.900 C 902.877 1466.185, 908.904 1476.363, 910.627 1481.419 C 912.219 1486.090, 912.200 1486.250, 908.868 1496.320 C 906.047 1504.846, 905.514 1507.881, 905.588 1515 C 905.722 1527.870, 908.685 1535.363, 923.690 1560.770 C 933.757 1577.816, 938.402 1587.421, 940.468 1595.460 C 942.373 1602.870, 942.371 1604.146, 940.455 1611.540 C 938.409 1619.436, 933.491 1627.435, 919.634 1645.408 C 901.597 1668.802, 895.740 1680.721, 887.405 1711 C 877.769 1746.001, 869.466 1764.368, 852.026 1789.259 C 841.687 1804.015, 817.705 1827.245, 799.289 1840.344 C 775.167 1857.500, 768.959 1865.392, 757.492 1893.480 C 750.070 1911.663, 743.268 1924.300, 731.099 1942.519 C 716.321 1964.641, 715.289 1968.218, 719.020 1984.367 C 721.054 1993.168, 721.151 1994.767, 720.056 2001.552 C 717.615 2016.684, 707.923 2041.965, 666.638 2140.885 L 653.123 2173.269 656.594 2190.385 C 664.812 2230.904, 666.273 2242.921, 663.855 2250.101 C 663.226 2251.970, 660.815 2256.200, 658.498 2259.500 C 654.478 2265.225, 651 2271.244, 651 2272.475 C 651 2276.696, 724.439 2277.992, 1133.500 2280.986 C 1328.847 2282.415, 1462.277 2283.986, 1473.330 2284.986 C 1489.993 2286.494, 1490.920 2288.528, 1483.056 2306.317 C 1476.793 2320.482, 1473.528 2330.556, 1470.877 2343.886 C 1467.781 2359.459, 1463.120 2371.891, 1450.964 2397 C 1444.974 2409.375, 1439.381 2422.182, 1438.536 2425.460 C 1436.494 2433.384, 1436.507 2454.280, 1438.560 2463 C 1441.868 2477.051, 1462.440 2515.115, 1488.013 2554.500 C 1518.043 2600.750, 1523.667 2612.806, 1528.484 2641.262 C 1531.494 2659.041, 1534.151 2665.007, 1542.642 2673.048 C 1559.752 2689.252, 1577.555 2696.972, 1591.400 2694.192 C 1603.446 2691.772, 1616.045 2683.029, 1626.561 2669.792 C 1629.828 2665.680, 1637.427 2655.463, 1643.448 2647.086 C 1655.509 2630.309, 1664.473 2619.978, 1669.933 2616.563 C 1677.665 2611.727, 1683.577 2611.486, 1707.951 2615.013 C 1725.279 2617.520, 1739.841 2615.971, 1777.872 2607.575 C 1816.220 2599.109, 1836.430 2596.034, 1863.500 2594.547 C 1877.125 2593.799, 1883.822 2594.889, 1894.311 2599.566 C 1916.864 2609.620, 1921.614 2610.343, 1976 2612.008 C 1994.975 2612.589, 2011.918 2613.287, 2013.651 2613.559 C 2015.944 2613.919, 2018.124 2615.614, 2021.649 2619.777 C 2029.706 2629.291, 2033.551 2631.912, 2039.527 2631.965 C 2044.212 2632.006, 2046.303 2630.747, 2054.606 2622.889 C 2060.566 2617.248, 2062.346 2616.337, 2068.500 2615.774 C 2070.817 2615.562, 2071.895 2614.692, 2073.237 2611.951 C 2076.041 2606.218, 2076.875 2597.520, 2078.169 2560.500 C 2079.135 2532.875, 2079.133 2503.163, 2078.161 2419.500 C 2077.484 2361.200, 2076.510 2297.750, 2075.998 2278.500 C 2073.137 2170.999, 2071.452 2117.423, 2065.410 1941.899 L 2060.811 1808.297 2067.875 1748.899 C 2071.760 1716.229, 2076.064 1679.150, 2077.439 1666.500 C 2078.814 1653.850, 2084.915 1601.875, 2090.998 1551 C 2164.693 934.615, 2193.171 677.937, 2217.479 411 C 2223.197 348.207, 2228.932 268.230, 2228.993 250.434 C 2228.997 249.293, 2226.115 245.165, 2222.557 241.214 C 2219.013 237.279, 2214.601 231.009, 2212.752 227.280 C 2210.904 223.551, 2206.534 216.497, 2203.043 211.604 C 2199.551 206.711, 2195.414 200.146, 2193.850 197.016 C 2192.166 193.647, 2189.991 190.861, 2188.520 190.191 C 2184.365 188.298, 2021.175 187.039, 1645.500 186.003 C 1453.550 185.473, 1289.412 185.031, 1280.750 185.020 L 1265 185 1264.987 187.750"
                fill="#000"
                strokeWidth="10"
              />
              {pixels.map((p) => {
                const isHovered = !locked && hoveredId === p.id;
                const isSelected = locked && selectedId === p.id;
                const showOutline = (isHovered || isSelected) && hasImages(p);

                const rectEl = (
                  <rect
                    x={p.x}
                    y={p.y}
                    width={p.size}
                    height={p.size}
                    className={`pixel ${hasImages(p) ? "pixel--with" : "pixel--empty"}`}
                    stroke="#000"
                    strokeWidth="20"
                    fill={hasImages(p) ? "#fff" : "#f3f4f6"}
                    onMouseEnter={() => handleEnter(p)}
                    onMouseLeave={() => handleLeave(p)}
                    onClick={() => handleClick(p)}
                  />
                );

                return (
                  <g key={p.id} style={{ cursor: "pointer" }}>
                    {hasImages(p) && (
                      <rect
                        x={p.x - 20}
                        y={p.y - 20}
                        width={p.size + 40}
                        height={p.size + 40}
                        fill="none"
                        stroke="#FFD700"
                        strokeWidth="20"
                        rx={4}
                        ry={4}
                        pointerEvents="none"
                      />
                    )}

                    {!hasImages(p) ? (
                      <Tooltip title="No images linked to this pixel" arrow>
                        {rectEl}
                      </Tooltip>
                    ) : (
                      rectEl
                    )}

                    {hasImages(p) && (
                      <image
                        href={p.images[0]?.url || p.images[0]}
                        x={p.x}
                        y={p.y}
                        width={p.size}
                        height={p.size}
                        preserveAspectRatio="xMidYMid slice"
                        style={{ pointerEvents: "none" }}
                      />
                    )}

                    {showOutline && (
                      <rect
                        x={p.x - 6}
                        y={p.y - 6}
                        width={p.size + 12}
                        height={p.size + 12}
                        fill="none"
                        stroke="#FFD700"
                        strokeWidth="1"
                        filter="url(#glow)"
                        vectorEffect="non-scaling-stroke"
                        pointerEvents="none"
                      />
                    )}
                  </g>
                );
              })}
            </svg>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default MississippiMap;
